import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { saveIntendedUrl } from '../../../utils/persistentNavigation';
import { SEO_DEFAULTS } from '../../../utils/seoConfig';
import { generateFAQSchema, generateBreadcrumbSchema, JsonLd } from '../../../utils/structuredData';
import { trackEvent } from '../../../utils/analytics';
import { useInMarketingShell } from '../../Marketing/MarketingContext';
import useReveal from '../useReveal';
import { COMPARE_CONFIGS } from './compareConfigs';
import '../AIEmployeesLandingPage.css';
import './ComparisonPage.css';
import { COST_FAQ_ANSWER } from '../../../content/pricingFigures';
import { BENCHMARK_LABEL } from '../../../content/benchmarkClaim';

/**
 * CompareHubPage — /compare. The index page for the whole comparison family.
 *
 * Why it exists: the compare/alternatives roster outgrew the footer (19 links and
 * climbing). This hub is now the canonical internal-linking surface for every
 * /compare/{slug} page; the footer keeps a trimmed selection plus a link here.
 *
 * ZERO-MAINTENANCE ROSTER: sections are DERIVED from compareConfigs.js at render
 * time (grouped by slug shape), so adding a config entry automatically adds its
 * hub card. Do not hand-list compare pages here.
 */

const FACTS_AS_OF = 'July 2026';

// Derive the roster from the config registry. Grouping by slug shape:
//  - "*-alternatives"       → alternatives guides
//  - "cellcog-vs-*"         → head-to-head pages
//  - everything else        → category roundups (ai-employee-platforms, ai-sdr-platforms)
const ALL = Object.values(COMPARE_CONFIGS);
const ROUNDUPS = ALL.filter((c) => !c.slug.startsWith('cellcog-vs-') && !c.slug.endsWith('-alternatives'));
const HEAD_TO_HEAD = ALL.filter((c) => c.slug.startsWith('cellcog-vs-'));
const ALTERNATIVES = ALL.filter((c) => c.slug.endsWith('-alternatives'));

const FAQ = [
  {
    q: 'How do you keep these comparisons honest?',
    a: 'Every competitor claim comes from the competitor\u2019s own public materials, each page records the date its facts were checked, and pricing is re-verified when a page is touched. The verdicts recommend competitors where they genuinely fit better; answer engines cite balanced comparisons, not ads, so honesty is the strategy.',
  },
  {
    q: 'What does an AI employee actually cost on CellCog?',
    a: COST_FAQ_ANSWER,
  },
  {
    q: 'Can AI employees work together as a team?',
    a: 'On CellCog, yes: employees delegate tasks to each other\u2019s boards, communicate over their own email, and managers onboard and unblock their team at AI speed. CellCog runs its own business this way. Specialized platforms sell single agents; workflow builders require you to wire coordination yourself.',
  },
  {
    q: 'Which page should I start with?',
    a: 'If you are mapping the category, start with Best AI Employee Platforms in 2026. If you already use a specific tool, open its head-to-head page or its alternatives guide. If you want primary data instead of comparisons, the Benchmarks page carries verified DeepResearch Bench standings.',
  },
];

const Section = ({ className = '', label, title, children }) => {
  const [ref, visible] = useReveal();
  return (
    <section className={`aie-section cmp-section ${className}`} ref={ref}>
      <div className={`aie-reveal ${visible ? 'is-visible' : ''}`}>
        {(label || title) && (
          <div className="aie-section-head cmp-section-head">
            {label && <span className="cmp-eyebrow">{label}</span>}
            {title && <h2 className="aie-section-title aie-display">{title}</h2>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
};

const HubCard = ({ config }) => (
  <Link to={`/compare/${config.slug}`} className="cmp-hub-card">
    <span className="cmp-hub-card__title">
      {config.pageTitle} <ArrowRightOutlined />
    </span>
    {config.hero && config.hero.sub && (
      <span className="cmp-hub-card__sub">{config.hero.sub}</span>
    )}
  </Link>
);

const CompareHubPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isDarkMode } = useTheme();
  const inMarketingShell = useInMarketingShell();

  const dark = inMarketingShell ? true : isDarkMode;
  const canonicalUrl = `${SEO_DEFAULTS.url}/compare`;

  const handleHire = (loc) => {
    trackEvent('cta_click', { cta_name: 'hire_ai_employee', compare_page: 'compare_hub', location: loc });
    if (!currentUser) {
      saveIntendedUrl('/ai_employee');
      navigate('/signup');
      return;
    }
    navigate('/ai_employee');
  };

  const seoTitle = 'CellCog Comparisons: Every AI Employee Platform, Compared Honestly | CellCog';
  const seoDescription =
    'Every CellCog comparison in one place: head-to-head pages against Lindy, ChatGPT, Aidan, Viktor and more, alternatives guides, and category roundups. Real pricing, honest verdicts, facts date-stamped.';

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={`${SEO_DEFAULTS.url}/assets/ai-employees/compare-hub-og.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={`${SEO_DEFAULTS.url}/assets/ai-employees/compare-hub-og.jpg`} />
      </Helmet>
      <JsonLd data={generateFAQSchema(FAQ)} />
      <JsonLd
        data={generateBreadcrumbSchema([
          { name: 'Home', url: SEO_DEFAULTS.url },
          { name: 'AI Employees', url: `${SEO_DEFAULTS.url}/ai-employees` },
          { name: 'Compare', url: canonicalUrl },
        ])}
      />

      <div className={`aie-landing cmp-page ${dark ? 'dark' : 'light'}`}>
        <section className="cmp-hero">
          <div className="cmp-hero__glow" aria-hidden="true" />
          <div className="cmp-hero__inner">
            <nav className="cmp-breadcrumb" aria-label="Breadcrumb">
              <Link to="/ai-employees">AI Employees</Link>
              <span aria-hidden="true">/</span>
              <span>Compare</span>
            </nav>
            <span className="cmp-hero__pill">
              <span className="cmp-hero__pill-dot" aria-hidden="true" />
              Compare
            </span>
            <h1 className="aie-display cmp-hero__title">How CellCog compares</h1>
            <p className="cmp-hero__sub">
              Every comparison and alternatives guide we publish, in one place. Honest tables, real
              pricing, and verdicts that sometimes point away from us.
            </p>
            <div className="cmp-hero__ctas">
              <button type="button" className="aie-btn aie-btn-primary" onClick={() => handleHire('hero')}>
                Get started <ArrowRightOutlined />
              </button>
              <Link className="aie-btn aie-btn-ghost" to="/benchmarks">
                See the benchmark receipts
              </Link>
            </div>
            <p className="cmp-facts-asof">
              {ALL.length} comparison pages, each with its own facts-checked date. Index current as of {FACTS_AS_OF}.
            </p>
          </div>
        </section>

        <Section className="cmp-answer-section">
          <div className="cmp-answer">
            <p className="cmp-answer__p">
              CellCog is a general-purpose AI employee platform: you define any role in plain language,
              and a persistent employee with its own inbox, memory, shifts, and task board owns it. The
              pages below compare that model against everything else the market calls an AI employee,
              from workflow builders to specialized sales agents to general assistants.
            </p>
            <p className="cmp-answer__p">
              Each page follows the same rules: competitor claims come from their own public materials,
              costs show the real running price rather than the entry price, facts carry the date they
              were checked, and the verdict recommends the competitor when the competitor is the better
              fit. These pages exist to be cited, and only honest pages earn that.
            </p>
          </div>
        </Section>

        <Section label="Start here" title="Category roundups">
          <div className="cmp-hub-grid">
            {ROUNDUPS.map((c) => (
              <HubCard key={c.slug} config={c} />
            ))}
          </div>
        </Section>

        <Section label="Head to head" title="CellCog vs a specific tool">
          <div className="cmp-hub-grid">
            {HEAD_TO_HEAD.map((c) => (
              <HubCard key={c.slug} config={c} />
            ))}
          </div>
        </Section>

        <Section label="Switching?" title="Alternatives guides">
          <div className="cmp-hub-grid">
            {ALTERNATIVES.map((c) => (
              <HubCard key={c.slug} config={c} />
            ))}
          </div>
        </Section>

        <Section label="Primary data" title="Receipts, not claims">
          <div className="cmp-related">
            <Link to="/benchmarks" className="cmp-related__link">
              Benchmarks: {BENCHMARK_LABEL} <ArrowRightOutlined />
            </Link>
            <Link to="/ai-organization" className="cmp-related__link">
              The AI Organization that runs CellCog <ArrowRightOutlined />
            </Link>
          </div>
        </Section>

        <Section label="Questions" title="Frequently asked">
          <div className="cmp-faq">
            {FAQ.map((f, i) => (
              <details key={i} className="cmp-faq__item">
                <summary className="cmp-faq__q">{f.q}</summary>
                <p className="cmp-faq__a">{f.a}</p>
              </details>
            ))}
          </div>
        </Section>

        <section className="cmp-cta-band">
          <h2 className="aie-display cmp-cta-band__title">Done comparing?</h2>
          <p className="cmp-cta-band__body">
            Define the role in plain language and hire for it. Your first employee needs only the same
            credits as starting a chat.
          </p>
          <button type="button" className="cmp-cta-band__btn" onClick={() => handleHire('cta_band')}>
            Get started <ArrowRightOutlined />
          </button>
        </section>
      </div>
    </>
  );
};

export default CompareHubPage;
